/**
 * ADIRA — in-cart variant switching: Weight + Grind (cart drawer + /cart page)
 *
 * Shopify's Cart AJAX API cannot change a line's VARIANT — /cart/change.js only takes
 * quantity/properties — so changing weight or grind means removing one line and adding
 * another. Each <option> already carries the TARGET VARIANT ID (resolved in Liquid), so
 * this file needs no variant lookup table: select.value IS the variant to swap to.
 *
 * ORDER: REMOVE first, then ADD. This is the opposite of what feels safe, and it is
 * deliberate. An add-first implementation was built and tested first, and it DOUBLE
 * CHARGED: adding the new grind makes Shopify re-allocate the "2nd bag 15% off"
 * Buy-X-Get-Y discount, which splits/merges cart lines and invalidates both the line key
 * and the line index captured at render time. The subsequent remove then silently missed
 * and the shopper ended up holding both grinds. Measured on the preview theme:
 * cart total went 38.85 -> 56.70 instead of staying put.
 *
 * Removing first keeps the line reference valid, because nothing has mutated the cart yet.
 * The remaining risk — the add failing after the remove — is covered two ways: options for
 * unavailable variants are rendered `disabled` in Liquid, and on failure we re-add the
 * original variant to put the cart back.
 *
 * Line identity: `line` (1-based index), matching the theme's own remove button
 * (`/onLineItemRemove/{{ item.index | plus: 1 }}`) and component-cart-items.js. NOT keys.
 *
 * Delegated from `document`: the cart section is morphed after every update, and <script>
 * tags injected via innerHTML never execute, so a listener bound inside the section markup
 * would die on the first update.
 */
(function () {
  if (window.__adiraCartGrindInit) return;
  window.__adiraCartGrindInit = true;

  var inFlight = false;

  function route(name, fallback) {
    try {
      if (window.Theme && Theme.routes && Theme.routes[name]) return Theme.routes[name];
    } catch (e) {}
    return fallback;
  }

  function jsonPost(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (res) {
      return res
        .json()
        .catch(function () {
          return {};
        })
        .then(function (data) {
          if (!res.ok) {
            var err = new Error((data && (data.description || data.message)) || 'Cart request failed');
            err.data = data;
            throw err;
          }
          return data;
        });
    });
  }

  /** Every cart section on the page, so the drawer and /cart stay in sync (as the theme does). */
  function sectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var id = el.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function morphCartSections(cart) {
    if (!cart || !cart.sections) return false;
    var morphedAny = false;
    Object.keys(cart.sections).forEach(function (id) {
      var html = cart.sections[id];
      if (!html) return;
      try {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var fresh = tmp.querySelector('cart-items-component');
        var current = document.querySelector('cart-items-component[data-section-id="' + id + '"]');
        if (fresh && current) {
          current.replaceWith(fresh);
          morphedAny = true;
        }
      } catch (e) {}
    });
    return morphedAny;
  }

  function announce(cart) {
    try {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            data: {
              sections: cart && cart.sections,
              itemCount: cart && cart.item_count,
              source: 'adira-grind-swap',
            },
          },
        })
      );
    } catch (e) {}
  }

  document.addEventListener('change', function (event) {
    var select = event.target && event.target.closest && event.target.closest('select[data-adira-opt]');
    if (!select) return;

    var newVariantId = select.value;
    var previousId = select.getAttribute('data-current-id');
    var line = parseInt(select.getAttribute('data-line'), 10);

    if (!newVariantId || !line || newVariantId === previousId) return;

    // One swap at a time: a second remove+add pair racing the first would act on line
    // indices that the first pair has already shifted.
    if (inFlight) {
      select.value = previousId;
      return;
    }
    inFlight = true;
    select.disabled = true;

    var quantity = parseInt(select.getAttribute('data-qty'), 10) || 1;
    var sellingPlan = select.getAttribute('data-selling-plan');
    var propertiesRaw = select.getAttribute('data-properties');
    var sections = sectionIds().join(',');
    var sectionsUrl = window.location.pathname;

    function buildLine(variantId) {
      var lineItem = { id: Number(variantId), quantity: quantity };
      if (sellingPlan) lineItem.selling_plan = Number(sellingPlan);
      if (propertiesRaw) {
        try {
          lineItem.properties = JSON.parse(propertiesRaw);
        } catch (e) {}
      }
      return lineItem;
    }

    function addVariant(variantId) {
      var body = { items: [buildLine(variantId)] };
      if (sections) {
        body.sections = sections;
        body.sections_url = sectionsUrl;
      }
      return jsonPost(route('cart_add_url', '/cart/add.js'), body);
    }

    var removeBody = { line: line, quantity: 0 };
    if (sections) {
      removeBody.sections = sections;
      removeBody.sections_url = sectionsUrl;
    }

    jsonPost(route('cart_change_url', '/cart/change.js'), removeBody)
      .then(function () {
        return addVariant(newVariantId);
      })
      .then(function (cart) {
        if (!morphCartSections(cart)) {
          // Never leave a stale cart on screen showing the wrong grind or price.
          window.location.reload();
          return;
        }
        announce(cart);
      })
      .catch(function () {
        // The remove may already have gone through, so restore the original grind
        // rather than leaving the shopper short an item.
        return addVariant(previousId)
          .catch(function () {})
          .then(function () {
            window.location.reload();
          });
      })
      .then(
        function () {
          inFlight = false;
        },
        function () {
          inFlight = false;
        }
      );
  });
})();
